<template>
  <!-- 停机窗口内：非管理员全屏维护门（盖住整个应用），窗口结束或公告下线自动消失 -->
  <div v-if="gateActive && gatePayload" class="mnt-gate">
    <div class="mnt-gate-inner">
      <div class="mnt-gate-gear">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v4m0 10v4M3 12h4m10 0h4M5.6 5.6l2.8 2.8m7.2 7.2 2.8 2.8M5.6 18.4l2.8-2.8m7.2-7.2 2.8-2.8M17 12a5 5 0 1 1-10 0 5 5 0 0 1 10 0Zm-3.4 0a1.6 1.6 0 1 1-3.2 0 1.6 1.6 0 0 1 3.2 0Z"/></svg>
      </div>
      <h1 class="mnt-gate-title">系统维护中</h1>
      <MaintenanceNoticeContent :payload="gatePayload" />
    </div>
  </div>

  <MaintenanceNoticeDialog
    v-model="visible"
    :payload="payload"
    :acknowledging="acknowledging"
    :ack-error="ackError"
    @acknowledge="acknowledge"
    @dismiss="dismiss"
  />
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import MaintenanceNoticeDialog from './MaintenanceNoticeDialog.vue'
import MaintenanceNoticeContent from './MaintenanceNoticeContent.vue'
import { messageApi } from '@/api/messages'
import { MAINTENANCE_EVENT_TYPE, maintenanceWindowPhase, parsePayload } from '@/utils/maintenanceNotice'

const props = defineProps({
  authenticated: { type: Boolean, default: false },
  identityId: { type: Number, default: null },
  routeKey: { type: String, default: '' },
  blocking: { type: Boolean, default: false },
  userRole: { type: String, default: '' },
})

const visible = ref(false)
const payload = ref(null)
const noticeId = ref(null)
const acknowledging = ref(false)
const ackError = ref('')
const gateActive = ref(false)
const gatePayload = ref(null)
const GATE_CACHE_KEY = 'biointelos-maintenance-gate-v1'

let checkedRoute = ''
let inflight = false
let pendingCheck = false
let pendingShow = false
let gateTimer = null
// attempted：本会话不再自动弹（含无法解析/已过期）；sessionDismissed：右上角关闭仅收起，跨会话未确认会再提醒。
const attempted = new Set()
const sessionDismissed = new Set()

const isAdmin = computed(() => String(props.userRole || '').toLowerCase() === 'admin')

// ── 全屏维护门：停机窗口内对非管理员生效；缓存进 localStorage，后端停机后刷新页面照样显示 ──

function readGateCache() {
  try {
    const raw = localStorage.getItem(GATE_CACHE_KEY)
    if (!raw) return null
    const entry = JSON.parse(raw)
    const payload = parsePayload(JSON.stringify(entry.payload || null))
    return payload ? { noticeId: entry.noticeId, payload } : null
  } catch { return null }
}

function writeGateCache(noticeId, payload) {
  try { localStorage.setItem(GATE_CACHE_KEY, JSON.stringify({ noticeId, payload })) } catch { /* 存储不可用时仅失去停机期间的记忆 */ }
}

function clearGateCache() {
  try { localStorage.removeItem(GATE_CACHE_KEY) } catch { /* ignore */ }
}

function evaluateGate() {
  if (isAdmin.value) {
    gateActive.value = false
    return
  }
  const cached = readGateCache()
  if (!cached) {
    gateActive.value = false
    return
  }
  gatePayload.value = cached.payload
  if (!noticeId.value) noticeId.value = cached.noticeId
  const phase = maintenanceWindowPhase(cached.payload, Date.now())
  gateActive.value = phase === 'active'
  if (gateActive.value || phase === 'expired') visible.value = false
  if (phase === 'expired') clearGateCache()
}

// ── 查询与确认 ──

async function check() {
  if (!props.authenticated || props.identityId == null) return
  if (gateActive.value || checkedRoute === props.routeKey) return
  if (inflight) {
    pendingCheck = true
    return
  }
  inflight = true
  try {
    // 不带 unreadOnly：全屏门按时间窗显示（与已读无关）；被下线的公告会从列表消失，用于判定撤门
    const data = await messageApi.list({ category: 'PLATFORM', pageSize: 50 })
    checkedRoute = props.routeKey
    const item = (data?.items || []).find(entry => entry.eventType === MAINTENANCE_EVENT_TYPE)
    if (!item || item.processingStatus === 'INVALIDATED') {
      // 公告被下线（列表里已不可见）：门与预告弹窗都收起
      gateActive.value = false
      clearGateCache()
      visible.value = false
      return
    }
    const parsed = parsePayload(item.content?.body)
    if (!parsed) { attempted.add(item.id); return }
    const phase = maintenanceWindowPhase(parsed, Date.now())
    if (isAdmin.value) return
    if (phase === 'expired') {
      gateActive.value = false
      clearGateCache()
      visible.value = false
      return
    }
    if (phase === 'active') {
      // 停机窗口内：非管理员直接进全屏维护门（与已读无关，窗口期始终显示）
      noticeId.value = item.id
      writeGateCache(item.id, parsed)
      evaluateGate()
      visible.value = false
      return
    }
    // 预告期：未读才弹一次；同时缓存公告供窗口开始后自动切换全屏门
    if (item.readAt) { attempted.add(item.id); return }
    if (attempted.has(item.id)) return
    attempted.add(item.id)
    noticeId.value = item.id
    payload.value = parsed
    writeGateCache(item.id, parsed)
    ackError.value = ''
    if (props.blocking) pendingShow = true
    else visible.value = true
  } catch {
    // 公告是附加信息，弱网和后台故障不阻塞研究流程；后续页面进入可重查。
  } finally {
    inflight = false
    if (pendingCheck) {
      pendingCheck = false
      void check()
    }
  }
}

async function acknowledge() {
  if (!noticeId.value || acknowledging.value) return
  acknowledging.value = true
  ackError.value = ''
  try {
    await messageApi.read(noticeId.value)
    visible.value = false
    // 与发版公告一致：确认后让消息中心刷新未读数。
    window.dispatchEvent(new Event('biointelos-messages-updated'))
  } catch {
    ackError.value = '已读状态未保存，请重试。'
  } finally {
    acknowledging.value = false
  }
}

function dismiss() {
  sessionDismissed.add(noticeId.value)
  visible.value = false
}

watch(() => props.blocking, blocking => {
  if (!blocking && pendingShow && payload.value && !visible.value) {
    pendingShow = false
    visible.value = true
  }
})
watch([() => props.routeKey, () => props.authenticated, () => props.identityId, () => props.userRole], () => {
  evaluateGate()
  check()
})
function onMessagesUpdated() { if (gateActive.value) void check() }
onMounted(() => {
  evaluateGate()
  gateTimer = setInterval(evaluateGate, 30000)
  window.addEventListener('biointelos-messages-updated', onMessagesUpdated)
  check()
})
onBeforeUnmount(() => {
  if (gateTimer) clearInterval(gateTimer)
  window.removeEventListener('biointelos-messages-updated', onMessagesUpdated)
})
</script>

<style scoped>
.mnt-gate {
  position: fixed;
  inset: 0;
  z-index: 4000;
  background: linear-gradient(180deg, #fbfcfe 0%, #f4f6fb 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  overflow: auto;
}
.mnt-gate-inner {
  max-width: 660px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
.mnt-gate-gear {
  width: 76px;
  height: 76px;
  border-radius: 22px;
  background: #fff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 10px 30px rgba(8, 64, 230, .10);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}
.mnt-gate-gear svg {
  width: 36px;
  height: 36px;
  fill: none;
  stroke: #0840e6;
  stroke-width: 1.7;
  stroke-linecap: round;
  stroke-linejoin: round;
  animation: mnt-gate-spin 5s linear infinite;
}
@keyframes mnt-gate-spin { to { transform: rotate(360deg); } }
.mnt-gate-title {
  font-size: 26px;
  font-weight: 700;
  color: #17233d;
  letter-spacing: -.01em;
  margin: 0 0 18px;
}
.mnt-gate-inner :deep(.mnt-body) {
  width: 100%;
  text-align: left;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 20px;
}
</style>
